"use client"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { authService } from "@/services/authService"
import type { AuthContextType, AuthUser } from "@/types/auth"

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = () => {
      try {
        const currentUser = authService.getCurrentUser()
        setUser(currentUser)
      } catch (error) {
        console.error("Error initializing auth:", error)
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    initAuth()
  }, [])

  // Update the login function signature to use 'username'
  const login = async (username: string, password: string): Promise<void> => {
    setLoading(true)
    try {
      const user = await authService.login(username, password)
      setUser(user)
    } catch (error) {
      setUser(null)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const logout = (): void => {
    authService.logout()
    setUser(null)
    // Redirect to home page after logout
    if (typeof window !== "undefined") {
      window.location.href = "/"
    }
  }

  // Update the AuthContextType interface
  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login, // This now expects (username, password)
    logout,
    loading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
